import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Caption1,
  Menu,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ChevronDown12Regular, Link16Regular, Mail16Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { AppDialog, ConfirmDialog } from "@/components/app-dialog";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { MockCaseMenu, type MockCase } from "@/features/access/MockCaseMenu";
import { FuiButton } from "@/components/fui-control";
import { useDialogLayer } from "@/components/ui/dialog-layer";

/** 샘플 명단. 초대 보내기·링크는 이 화면 안에서만 동작한다. */
const DIRECTORY = [
  { id: "res_kim_pm", name: "김PM", email: "kim.pm@wavex.io", title: "프로젝트 총괄 PM" },
  { id: "res_lee_arch", name: "이아키", email: "lee.arch@freelance.net", title: "수석 솔루션 아키텍트" },
  { id: "res_park_dev", name: "박엔진", email: "park.engine@partner-c.com", title: "코어 백엔드 PL" },
  { id: "res_choi", name: "최기획", email: "choi.plan@wavex.io", title: "업무 분석 / BA 파트장" },
  { id: "res_jung", name: "정프론트", email: "jung.ui@wavex.io", title: "웹 프론트엔드 개발자" },
  { id: "res_kang", name: "강인프라", email: "kang.cloud@freelance.net", title: "클라우드 / DevOps 엔지니어" },
  { id: "res_song", name: "송DB", email: "song.dba@wavex.io", title: "데이터 모델러 / DBA" },
  { id: "res_han", name: "한로봇", email: "han.robot@wavex.io", title: "로봇 라인 총괄 PL" },
] as const;

type InviteRole = "edit" | "view";

/** 새로 고른 사람의 기본 권한. 이번 목록에서 바꾼다. */
const DEFAULT_ROLE: InviteRole = "edit";

type RosterPerson = {
  id: string;
  name: string;
  email: string;
  role: InviteRole;
};

/** 대화상자를 열었을 때 보이는 이번 초대 목업. */
const SESSION: RosterPerson[] = [
  { id: "res_lee_arch", name: "이아키", email: "lee.arch@freelance.net", role: "edit" },
  { id: "res_jung", name: "정프론트", email: "jung.ui@wavex.io", role: "view" },
  { id: "res_song", name: "송DB", email: "song.dba@wavex.io", role: "edit" },
  { id: "res_han", name: "한로봇", email: "han.robot@wavex.io", role: "view" },
];

type Suggestion =
  | { kind: "person"; person: (typeof DIRECTORY)[number]; taken: boolean }
  | { kind: "email"; email: string; taken: boolean };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_LABEL: Record<InviteRole, string> = {
  edit: "편집할 수 있음",
  view: "볼 수 있음",
};

const ROLE_SHORT: Record<InviteRole, string> = {
  edit: "편집",
  view: "보기",
};


/** 이번 목록에 이미 있으면 다시 고르지 못한다. */
function blockHint(taken: boolean) {
  return taken ? "추가됨" : null;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
  },
  field: {
    display: "flex",
    alignItems: "stretch",
    flex: 1,
    minWidth: 0,
    minHeight: "36px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    "&:hover": {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
    },
    "&:has(:focus-visible)": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "&:focus-within": {
      borderTopColor: tokens.colorCompoundBrandStroke,
      borderRightColor: tokens.colorCompoundBrandStroke,
      borderBottomColor: tokens.colorCompoundBrandStroke,
      borderLeftColor: tokens.colorCompoundBrandStroke,
    },
  },
  fieldError: {
    borderTopColor: tokens.colorPaletteRedBorderActive,
    borderRightColor: tokens.colorPaletteRedBorderActive,
    borderBottomColor: tokens.colorPaletteRedBorderActive,
    borderLeftColor: tokens.colorPaletteRedBorderActive,
  },
  query: {
    flex: 1,
    width: "100%",
    minWidth: 0,
    height: "34px",
    padding: "0 8px",
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    "::placeholder": {
      color: tokens.colorNeutralForeground3,
    },
  },
  roleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    height: "24px",
    padding: "0 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground2,
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    whiteSpace: "nowrap",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  menuList: {
    fontWeight: tokens.fontWeightRegular,
  },
  // MenuPopover mounted into a dialog layer has z-index:auto; dialog content is z-50.
  // Body pointer-events are none while a modal is open, so the portaled menu must opt back in.
  menuPopover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
  suggestPopover: {
    zIndex: 100,
    pointerEvents: "auto",
    boxSizing: "border-box",
    maxHeight: "320px",
    overflow: "auto",
    padding: "4px",
  },
  suggestEmpty: {
    padding: "6px 8px",
    color: tokens.colorNeutralForeground3,
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    minHeight: "36px",
    padding: "4px 6px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    textAlign: "left",
    fontWeight: tokens.fontWeightRegular,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  optionOn: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  optionBlocked: {
    cursor: "default",
    ":hover": {
      backgroundColor: "transparent",
    },
  },
  optionText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  optionName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "13px",
    lineHeight: "18px",
  },
  optionMeta: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
  optionStatus: {
    flexShrink: 0,
    marginLeft: "8px",
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },
  peopleList: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    maxHeight: "320px",
    overflow: "auto",
    padding: "4px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  person: {
    display: "grid",
    gridTemplateColumns: "24px minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: "8px",
    minHeight: "40px",
    padding: "2px 6px",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  personText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  personName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  personMeta: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
  personAside: {
    display: "grid",
    gridTemplateColumns: "56px 24px",
    alignItems: "center",
    columnGap: "4px",
    flexShrink: 0,
  },
  roleSlot: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  actionSlot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

function inviteUrl(workspaceName: string) {
  const slug = workspaceName.trim().toLowerCase().replace(/\s+/g, "-") || "workspace";
  return `https://wit.local/join/${encodeURIComponent(slug)}`;
}

export function InviteDialog({
  open,
  onClose,
  workspaceName,
  empty = false,
}: {
  open: boolean;
  onClose: () => void;
  workspaceName: string;
  /** 이번 목록이 비어 있는 표본. */
  empty?: boolean;
}) {
  const [mock, setMock] = useState(empty ? "empty" : "session");
  const preset = INVITE_CASES.find((item) => item.id === mock) ?? INVITE_CASES[0]!;
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="md"
      className="overflow-visible"
      title={`${workspaceName} 초대`}
      titleAside={<MockCaseMenu cases={INVITE_CASES} onPick={setMock} />}
    >
      {open && (
        <InvitePanel
          key={mock}
          workspaceName={workspaceName}
          empty={preset.empty}
          initialQuery={preset.query}
        />
      )}
    </AppDialog>
  );
}

const INVITE_CASES: Array<MockCase & { empty?: boolean; query?: string }> = [
  { id: "session", label: "이번 초대가 있음" },
  { id: "empty", label: "아직 추가한 사람이 없음", empty: true },
  { id: "miss", label: "검색이 비어 있음", query: "없는사람" },
];

export function InvitePanel({
  workspaceName,
  empty = false,
  initialQuery = "",
}: {
  workspaceName: string;
  /** 이번 목록이 비어 있는 표본. */
  empty?: boolean;
  initialQuery?: string;
}) {
  const styles = useStyles();
  const layer = useDialogLayer();
  const uid = useId();
  const listId = `${uid}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  const [roster, setRoster] = useState<RosterPerson[]>(() => (empty ? [] : SESSION));
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<RosterPerson | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const takenEmails = useMemo(() => new Set(roster.map((person) => person.email.toLowerCase())), [roster]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const matched = DIRECTORY.filter((p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q));
    const items: Suggestion[] = matched.map((person) => ({
      kind: "person",
      person,
      taken: takenEmails.has(person.email.toLowerCase()),
    }));
    const typed = query.trim();
    if (EMAIL.test(typed) && !items.some((item) => item.kind === "person" && item.person.email.toLowerCase() === typed.toLowerCase())) {
      items.push({ kind: "email", email: typed, taken: takenEmails.has(typed.toLowerCase()) });
    }
    return items;
  }, [query, takenEmails]);

  const searching = query.trim().length > 0;
  const activeIndex = (() => {
    if (!suggestions.length) return 0;
    const clamped = Math.min(active, suggestions.length - 1);
    if (!suggestions[clamped]?.taken) return clamped;
    const next = suggestions.findIndex((item) => !item.taken);
    return next < 0 ? clamped : next;
  })();

  const invitePerson = (next: { id: string; name: string; email: string; guest?: boolean }) => {
    const emailKey = next.email.toLowerCase();
    if (roster.some((row) => row.email.toLowerCase() === emailKey)) return;
    const displayName = next.guest ? next.email : next.name;
    setRoster((cur) => {
      if (cur.some((row) => row.email.toLowerCase() === emailKey)) return cur;
      return [...cur, { id: next.id, name: displayName, email: next.email, role: DEFAULT_ROLE }];
    });
    setQuery("");
    setError(null);
    setActive(0);
    inputRef.current?.focus();
  };

  const addSuggestion = (item: Suggestion) => {
    if (item.taken) return;
    if (item.kind === "person") {
      invitePerson({ id: item.person.id, name: item.person.name, email: item.person.email });
      return;
    }
    invitePerson({ id: `guest:${item.email.toLowerCase()}`, name: item.email, email: item.email, guest: true });
  };

  const changeRole = (personId: string, next: InviteRole) => {
    setRoster((cur) => cur.map((row) => (row.id === personId && row.role !== next ? { ...row, role: next } : row)));
  };

  const addActive = () => {
    const item = suggestions[activeIndex];
    if (!item || item.taken) {
      if (query.trim() && !item) setError("이름 또는 메일 주소를 입력하세요.");
      return;
    }
    addSuggestion(item);
  };

  const stepActive = (dir: 1 | -1) => {
    if (!suggestions.length) return;
    setActive((cur) => {
      for (let n = 1; n <= suggestions.length; n++) {
        const i = (cur + dir * n + suggestions.length) % suggestions.length;
        if (!suggestions[i]?.taken) return i;
      }
      return cur;
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl(workspaceName));
    } catch {
      /* 샘플이라 복사 권한이 없어도 버튼 상태만 바꾼다. */
    }
    if (!alive.current) return;
    setCopied(true);
    window.setTimeout(() => {
      if (alive.current) setCopied(false);
    }, 1600);
  };

  const confirmPending = () => {
    if (!pending) return;
    setRoster((cur) => cur.filter((row) => row.id !== pending.id));
    setPending(null);
  };

  return (
    <div className={styles.root}>
      <Popover
        open={searching}
        inline
        trapFocus={false}
        positioning={{ strategy: "fixed", position: "below", align: "start", matchTargetSize: "width", fallbackPositions: ["above"] }}
        onOpenChange={(event, data) => {
          if (data.open) return;
          const target = event.target;
          if (target instanceof Node && inputRef.current?.contains(target)) return;
          if (target instanceof Element && target.closest("[role='listbox']")) return;
          setQuery("");
        }}
      >
        <PopoverTrigger disableButtonEnhancement>
          <div className={mergeClasses(styles.field, Boolean(error) && styles.fieldError)}>
            <input
              ref={inputRef}
              className={styles.query}
              value={query}
              placeholder="받는 사람"
              aria-label="받는 사람"
              aria-autocomplete="list"
              aria-controls={listId}
              aria-expanded={searching}
              aria-activedescendant={searching && suggestions[activeIndex] ? `${uid}-opt-${activeIndex}` : undefined}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" && searching && suggestions.length) {
                  e.preventDefault();
                  stepActive(1);
                } else if (e.key === "ArrowUp" && searching && suggestions.length) {
                  e.preventDefault();
                  stepActive(-1);
                } else if ((e.key === "Enter" || e.key === ",") && query.trim()) {
                  e.preventDefault();
                  addActive();
                } else if (e.key === "Escape" && query) {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery("");
                }
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverSurface className={styles.suggestPopover} aria-hidden={!searching}>
          <div id={listId} role="listbox" aria-label="초대할 사람">
            {suggestions.length === 0 ? (
              <Caption1 className={styles.suggestEmpty}>일치하는 사람이 없습니다</Caption1>
            ) : (
              suggestions.map((item, index) => {
                const blocked = item.taken;
                const hint = blockHint(item.taken);
                const label = item.kind === "person" ? item.person.name : item.email;
                const meta = item.kind === "person" ? `${item.person.email} · ${item.person.title}` : "메일로 초대";
                return (
                  <button
                    key={item.kind === "person" ? item.person.id : item.email}
                    id={`${uid}-opt-${index}`}
                    type="button"
                    role="option"
                    aria-selected={!blocked && index === activeIndex}
                    aria-disabled={blocked}
                    className={mergeClasses(styles.option, blocked && styles.optionBlocked, !blocked && index === activeIndex && styles.optionOn)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => {
                      if (!blocked) setActive(index);
                    }}
                    onClick={() => addSuggestion(item)}
                  >
                    {item.kind === "person" ? (
                      <PersonAvatar name={item.person.name} size={28} tip={false} />
                    ) : (
                      <Avatar icon={<Mail16Regular />} size={28} color="neutral" aria-hidden />
                    )}
                    <span className={styles.optionText}>
                      <span className={styles.optionName}>{label}</span>
                      <Caption1 className={styles.optionMeta}>{meta}</Caption1>
                    </span>
                    {hint && <Caption1 className={styles.optionStatus}>{hint}</Caption1>}
                  </button>
                );
              })
            )}
          </div>
        </PopoverSurface>
      </Popover>

      {error && (
        <Caption1 role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>
          {error}
        </Caption1>
      )}

      <div className={styles.peopleList} role="list" aria-label="추가된 사람">
          {roster.length === 0 ? (
            <GhostEmptyState compact title="아직 추가된 사람이 없습니다" description="이름이나 메일로 초대하면 여기에 모입니다." />
          ) : (
            roster.map((person) => {
              const name = person.name;
              const email = person.email && person.email !== person.name ? person.email : "";
              return (
                <div key={person.id} role="listitem" className={styles.person}>
                  <PersonAvatar name={person.name} size={24} tip={false} />
                  <span className={styles.personText}>
                    <span className={styles.personName} title={name}>{name}</span>
                    {email ? <Caption1 className={styles.personMeta} title={email}>{email}</Caption1> : null}
                  </span>
                  <span className={styles.personAside}>
                    <span className={styles.roleSlot}>
                      <Menu
                        mountNode={layer}
                        positioning={{ strategy: "fixed", position: "below", align: "end" }}
                        checkedValues={{ role: [person.role] }}
                        onCheckedValueChange={(_, data) => {
                          const next = data.checkedItems[0];
                          if (next === "edit" || next === "view") changeRole(person.id, next);
                        }}
                      >
                        <MenuTrigger disableButtonEnhancement>
                          <button type="button" className={styles.roleBtn} aria-label={`${name} 권한, ${ROLE_LABEL[person.role]}`}>
                            {ROLE_SHORT[person.role]}
                            <ChevronDown12Regular />
                          </button>
                        </MenuTrigger>
                        <MenuPopover className={styles.menuPopover}>
                          <MenuList className={styles.menuList}>
                            <MenuItemRadio name={`role-${person.id}`} value="edit">
                              편집할 수 있음
                            </MenuItemRadio>
                            <MenuItemRadio name={`role-${person.id}`} value="view">
                              볼 수 있음
                            </MenuItemRadio>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </span>
                    <span className={styles.actionSlot}>
                      <Menu mountNode={layer} positioning={{ strategy: "fixed", position: "below", align: "end" }}>
                        <MenuTrigger disableButtonEnhancement>
                          <button type="button" className={styles.menuBtn} aria-label={`${name} 동작`}>
                            <MoreHorizontal16Regular />
                          </button>
                        </MenuTrigger>
                        <MenuPopover className={styles.menuPopover}>
                          <MenuList className={styles.menuList}>
                            <MenuItem onClick={() => setPending(person)}>빼기</MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </span>
                  </span>
                </div>
              );
            })
          )}
      </div>

      <div className={styles.footer}>
        <FuiButton appearance="outline" density="dialog" icon={<Link16Regular />} onClick={() => void copyLink()}>
          {copied ? "복사됨" : "링크 복사"}
        </FuiButton>
      </div>
      <ConfirmDialog
        open={pending !== null}
        title="목록에서 빼기"
        message="이 목록에서 빠집니다."
        cancelLabel="그대로 두기"
        confirmLabel="빼기"
        onCancel={() => setPending(null)}
        onConfirm={confirmPending}
      />
    </div>
  );
}
